import { useEffect, useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { syncTodayHealthKitData, getHealthKitSettings } from "@/lib/healthkit";
import { detectNativeApp, waitForHealthKitSDK } from "@/lib/platform";

/**
 * HealthKitSync Component
 * 
 * This component handles automatic syncing of HealthKit data on app startup.
 * It checks if the user has HealthKit enabled and syncs data in the background.
 * 
 * IMPORTANT: Only runs in native iOS app, not in web browsers.
 */
const HealthKitSync = () => {
  const { user, isLoadingAuth } = useAuth();
  const [hasSynced, setHasSynced] = useState(false);

  useEffect(() => {
    const syncHealthKit = async () => {
      // Wait for auth to complete
      if (isLoadingAuth || !user || hasSynced) return;

      try {
        // Detect if we're in a native app using the new async method
        const detection = await detectNativeApp();
        
        if (!detection.isNative) {
          // Not in native app - no need to log or sync
          setHasSynced(true);
          return;
        }
        
        console.log('[HealthKit Sync] Native app detected, checking settings...');
        
        // Wait for SDK to load (up to 5 seconds)
        const sdkLoaded = await waitForHealthKitSDK(5000, 500);
        
        if (!sdkLoaded) {
          console.log('[HealthKit Sync] HealthKit SDK not available');
          setHasSynced(true);
          return;
        }
        
        // Check if user has HealthKit enabled
        const settings = await getHealthKitSettings(user.id);
        
        if (settings && settings.enabled) {
          console.log("[HealthKit Sync] Syncing HealthKit data...");
          
          // Sync in background without blocking UI
          syncTodayHealthKitData(user.id)
            .then((success) => {
              if (success) {
                console.log("[HealthKit Sync] Completed successfully");
              } else {
                console.log("[HealthKit Sync] No data to sync or sync failed");
              }
            })
            .catch((error) => {
              console.error("[HealthKit Sync] Error:", error);
              // Don't throw - fail silently for background syncs
            });
          
          setHasSynced(true);
        } else {
          setHasSynced(true);
        }
      } catch (error) {
        console.error("[HealthKit Sync] Error checking settings:", error);
        setHasSynced(true); // Mark as synced to prevent retry loops
      }
    };

    syncHealthKit();
  }, [user, isLoadingAuth, hasSynced]);

  // This component doesn't render anything
  return null;
};

export default HealthKitSync;
